// src/services/variantService.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  orderBy,
  query,
  where,
  serverTimestamp,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { VariantType, VariantOption, VariantTypeFormData } from '../../../enums/product';

const VARIANT_TYPES_COLLECTION = 'variantTypes';

export interface PaginatedVariantTypesResult {
  variantTypes: VariantType[];
  totalCount: number;
  // Kept for interface compatibility — always null now (seek-based pagination
  // doesn't need callers to pass cursors back in).
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export interface VariantTypeStats {
  total: number;
  active: number;
  inactive: number;
  totalOptions: number;
}

/* ===================== GET VARIANT TYPES PAGINATED =====================
 *
 * FIX: Replaced cursor-based pagination (startAfter / endBefore /
 * limitToLast) with a two-step cursor-seek so any page can be fetched
 * in any order (page 1 → page 5 → page 2 all work correctly).
 *
 * HOW IT WORKS
 * ─────────────
 * Firestore's client SDK has no offset(). The classic startAfter cursor
 * only works sequentially. To jump to page N we:
 *
 *   Step 1 — fetch only the first (page-1)*pageSize doc IDs (lightweight
 *             seek). The last doc from this set becomes our startAfter cursor.
 *   Step 2 — fetch the real pageSize docs starting after that cursor.
 *
 * For page 1 the seek is skipped entirely (skip count = 0).
 * ========================================================= */
export const fetchVariantTypesPaginated = async (
  pageSize: number = 10,
  page: number = 1,
  searchQuery?: string
): Promise<PaginatedVariantTypesResult> => {
  try {
    const variantTypes: VariantType[] = [];
    const collectionRef = collection(db, VARIANT_TYPES_COLLECTION);
    const orderConstraint = orderBy('createdAt', 'desc');

    // ── Total count (0 doc reads) ──────────────────────────────────────────
    const countSnapshot = await getCountFromServer(query(collectionRef));
    const totalCount = countSnapshot.data().count;

    // ── Step 1: cursor seek ────────────────────────────────────────────────
    let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
    const skipCount = (page - 1) * pageSize;

    if (skipCount > 0) {
      const seekSnapshot = await getDocs(
        query(collectionRef, orderConstraint, limit(skipCount))
      );
      if (!seekSnapshot.empty) {
        cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
      }
    }

    // ── Step 2: fetch the real page ────────────────────────────────────────
    const pageQuery: Query<DocumentData> = cursorDoc
      ? query(collectionRef, orderConstraint, startAfter(cursorDoc), limit(pageSize))
      : query(collectionRef, orderConstraint, limit(pageSize));

    const snapshot = await getDocs(pageQuery);

    // ── Map + client-side search filter ───────────────────────────────────
    snapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      const variantType: VariantType = {
        id: docSnap.id,
        name: data.name || '',
        isRequired: data.isRequired || false,
        allowsMultiple: data.allowsMultiple || false,
        isActive: data.isActive !== undefined ? data.isActive : true,
        options: data.options || [],
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        createdBy: data.createdBy,
      };

      if (searchQuery && searchQuery.trim() !== '') {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch =
          variantType.name.toLowerCase().includes(searchLower) ||
          variantType.options.some(
            opt =>
              opt.name.toLowerCase().includes(searchLower) ||
              (opt.value && opt.value.toLowerCase().includes(searchLower))
          );
        if (matchesSearch) variantTypes.push(variantType);
      } else {
        variantTypes.push(variantType);
      }
    });

    return {
      variantTypes,
      totalCount,
      lastVisible: null,   // no longer used by callers
      firstVisible: null,  // no longer used by callers
    };
  } catch (error) {
    console.error('Error fetching paginated variant types:', error);
    throw error;
  }
};

/* ===================== GET VARIANT TYPE STATS =====================
 * Uses getCountFromServer() for total/active/inactive (free reads).
 * totalOptions requires reading all documents since it is an array
 * field — there is no Firestore aggregate for array lengths.
 * ================================================================= */
export const fetchVariantTypeStats = async (): Promise<VariantTypeStats> => {
  try {
    const col = collection(db, VARIANT_TYPES_COLLECTION);

    const [totalSnap, activeSnap, inactiveSnap, allDocsSnap] = await Promise.all([
      getCountFromServer(query(col)),
      getCountFromServer(query(col, where('isActive', '==', true))),
      getCountFromServer(query(col, where('isActive', '==', false))),
      getDocs(query(col)),
    ]);

    const totalOptions = allDocsSnap.docs.reduce(
      (sum, d) => sum + ((d.data().options as any[])?.length || 0),
      0
    );

    return {
      total: totalSnap.data().count,
      active: activeSnap.data().count,
      inactive: inactiveSnap.data().count,
      totalOptions,
    };
  } catch (error) {
    console.error('Error fetching variant type stats:', error);
    throw error;
  }
};

/* ===================== CREATE VARIANT TYPE ===================== */
export const createVariantType = async (
  formData: VariantTypeFormData,
  userId: string
): Promise<{ success: boolean; message: string; variantTypeId?: string }> => {
  try {
    if (!formData.name.trim()) throw new Error('Variant type name is required');

    // Duplicate check — targeted query instead of full collection read
    const dupSnap = await getDocs(
      query(
        collection(db, VARIANT_TYPES_COLLECTION),
        where('name', '==', formData.name.trim())
      )
    );
    if (!dupSnap.empty) {
      return { success: false, message: 'A variant type with this name already exists' };
    }

    const docRef = await addDoc(collection(db, VARIANT_TYPES_COLLECTION), {
      name: formData.name.trim(),
      isActive: formData.isActive !== undefined ? formData.isActive : true,
      options: formData.options.map(option => ({
        id: generateId(),
        name: option.name.trim(),
        value: option.value?.trim() || null,
        isActive: option.isActive !== undefined ? option.isActive : true,
      })),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userId,
    });

    return { success: true, message: 'Variant type created successfully!', variantTypeId: docRef.id };
  } catch (error: any) {
    console.error('Error creating variant type:', error);
    return { success: false, message: error.message || 'Failed to create variant type.' };
  }
};

/* ===================== UPDATE VARIANT TYPE ===================== */
export const updateVariantType = async (
  variantTypeId: string,
  formData: VariantTypeFormData
): Promise<{ success: boolean; message: string }> => {
  try {
    if (!formData.name.trim()) throw new Error('Variant type name is required');

    // Duplicate check — targeted query instead of full collection read
    const dupSnap = await getDocs(
      query(
        collection(db, VARIANT_TYPES_COLLECTION),
        where('name', '==', formData.name.trim())
      )
    );
    const duplicate = dupSnap.docs.find(d => d.id !== variantTypeId);
    if (duplicate) {
      return { success: false, message: 'Another variant type with this name already exists' };
    }

    const variantTypeRef = doc(db, VARIANT_TYPES_COLLECTION, variantTypeId);
    await updateDoc(variantTypeRef, {
      name: formData.name.trim(),
      isActive: formData.isActive !== undefined ? formData.isActive : true,
      options: formData.options.map(option => ({
        id: option.id || generateId(),
        name: option.name.trim(),
        value: option.value?.trim() || null,
        isActive: option.isActive !== undefined ? option.isActive : true,
      })),
      updatedAt: serverTimestamp(),
    });

    return { success: true, message: 'Variant type updated successfully!' };
  } catch (error: any) {
    console.error('Error updating variant type:', error);
    return { success: false, message: error.message || 'Failed to update variant type.' };
  }
};

/* ===================== DELETE VARIANT TYPE ===================== */
export const deleteVariantType = async (
  variantTypeId: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const variantTypeRef = doc(db, VARIANT_TYPES_COLLECTION, variantTypeId);
    const variantTypeSnap = await getDoc(variantTypeRef);

    if (!variantTypeSnap.exists()) {
      return { success: false, message: 'Variant type not found.' };
    }

    await deleteDoc(variantTypeRef);
    return { success: true, message: 'Variant type deleted successfully!' };
  } catch (error: any) {
    console.error('Error deleting variant type:', error);
    return { success: false, message: error.message || 'Failed to delete variant type.' };
  }
};

/* ===================== TOGGLE STATUS ===================== */
export const toggleVariantTypeStatus = async (
  variantTypeId: string,
  isActive: boolean
): Promise<{ success: boolean; message: string }> => {
  try {
    const variantTypeRef = doc(db, VARIANT_TYPES_COLLECTION, variantTypeId);
    const snap = await getDoc(variantTypeRef);
    if (!snap.exists()) return { success: false, message: 'Variant type not found.' };

    await updateDoc(variantTypeRef, { isActive, updatedAt: serverTimestamp() });
    return { success: true, message: `Variant type ${isActive ? 'activated' : 'deactivated'} successfully!` };
  } catch (error: any) {
    console.error('Error toggling variant type status:', error);
    return { success: false, message: error.message || 'Failed to update status.' };
  }
};

/* ===================== HELPERS ===================== */
const generateId = (): string =>
  `opt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/* ===================== LEGACY / OTHER CONSUMERS ===================== */

/**
 * @deprecated Use fetchVariantTypesPaginated instead.
 */
export const fetchVariantTypes = async (): Promise<VariantType[]> => {
  try {
    const q = query(collection(db, VARIANT_TYPES_COLLECTION), orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => {
      const data = d.data();
      return {
        id: d.id,
        name: data.name,
        isRequired: data.isRequired || false,
        allowsMultiple: data.allowsMultiple || false,
        isActive: data.isActive !== undefined ? data.isActive : true,
        options: data.options || [],
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        createdBy: data.createdBy,
      } as VariantType;
    });
  } catch (error) {
    console.error('Error fetching variant types:', error);
    throw error;
  }
};

export const fetchActiveVariantTypes = async (): Promise<VariantType[]> => {
  try {
    const q = query(
      collection(db, VARIANT_TYPES_COLLECTION),
      where('isActive', '==', true),
      orderBy('name', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => {
      const data = d.data();
      return {
        id: d.id,
        name: data.name,
        isRequired: data.isRequired || false,
        allowsMultiple: data.allowsMultiple || false,
        isActive: true,
        options: (data.options || []).filter((opt: VariantOption) => opt.isActive !== false),
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        createdBy: data.createdBy,
      } as VariantType;
    });
  } catch (error) {
    console.error('Error fetching active variant types:', error);
    throw error;
  }
};

export const fetchVariantTypeById = async (variantTypeId: string): Promise<VariantType | null> => {
  try {
    const snap = await getDoc(doc(db, VARIANT_TYPES_COLLECTION, variantTypeId));
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      id: snap.id,
      name: data.name,
      isRequired: data.isRequired || false,
      allowsMultiple: data.allowsMultiple || false,
      isActive: data.isActive !== undefined ? data.isActive : true,
      options: data.options || [],
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      createdBy: data.createdBy,
    } as VariantType;
  } catch (error) {
    console.error('Error fetching variant type:', error);
    throw error;
  }
};

export const fetchVariantTypesForDropdown = async (): Promise<{ value: string; label: string }[]> => {
  try {
    const variantTypes = await fetchActiveVariantTypes();
    return variantTypes.map(type => ({ value: type.name, label: type.name }));
  } catch (error) {
    console.error('Error getting variant types for dropdown:', error);
    throw error;
  }
};

export const fetchVariantOptionsForType = async (variantTypeName: string): Promise<VariantOption[]> => {
  try {
    const variantTypes = await fetchActiveVariantTypes();
    const variantType = variantTypes.find(type => type.name === variantTypeName);
    if (!variantType) return [];
    return variantType.options.filter(opt => opt.isActive !== false);
  } catch (error) {
    console.error('Error getting variant options for type:', error);
    throw error;
  }
};

/**
 * Backwards-compatible class wrapper so existing callers that use
 * `VariantService.someMethod()` continue to work without changes.
 */
export class VariantService {
  static getVariantTypesPaginated    = fetchVariantTypesPaginated;
  static getVariantTypeStats         = fetchVariantTypeStats;
  static createVariantType           = createVariantType;
  static updateVariantType           = updateVariantType;
  static deleteVariantType           = deleteVariantType;
  static toggleVariantTypeStatus     = toggleVariantTypeStatus;
  /** @deprecated */ static getVariantTypes = fetchVariantTypes;
  static getActiveVariantTypes       = fetchActiveVariantTypes;
  static getVariantType              = fetchVariantTypeById;
  static getVariantTypesForDropdown  = fetchVariantTypesForDropdown;
  static getVariantOptionsForType    = fetchVariantOptionsForType;
  static async isVariantTypeInUse(_variantTypeId: string): Promise<boolean> { return false; }
}